import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import VideocamIcon from "@mui/icons-material/Videocam";
import { apiClient, ensureTokenIsSet } from "../../services/apiClient";
import {
  PresenceCameraConfig,
  PresenceCameraRole,
} from "../../types/api-types";

const emptyCameraConfig: PresenceCameraConfig = {
  enabled: false,
  baseUrl: "",
  key: "",
  cid: "hobbygarage-ki-bot",
  warm: true,
  cameras: [],
};

const CAMERA_ROLE_LABELS: Record<PresenceCameraRole, string> = {
  gate: "Tor (offen/zu)",
  presence: "Person (z. B. Büro)",
  both: "Tor + Person",
};

const PresenceCameraSettings: React.FC = () => {
  const [cameraConfig, setCameraConfig] = useState<PresenceCameraConfig>(emptyCameraConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        ensureTokenIsSet();
        setLoading(true);
        setCameraConfig({ ...emptyCameraConfig, ...(await apiClient.getPresenceCameraConfig()) });
        setError(null);
      } catch (err: any) {
        setError(err.message || "Kamera-Konfiguration konnte nicht geladen werden");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const saveCameraConfig = async () => {
    try {
      ensureTokenIsSet();
      setSaving(true);
      setSuccess(null);
      setError(null);
      setCameraConfig({
        ...emptyCameraConfig,
        ...(await apiClient.updatePresenceCameraConfig(cameraConfig)),
      });
      setSuccess("Kamera-Konfiguration gespeichert.");
    } catch (err: any) {
      setError(err.message || "Kamera-Konfiguration konnte nicht gespeichert werden");
    } finally {
      setSaving(false);
    }
  };

  const updateCameraAt = (
    index: number,
    patch: Partial<PresenceCameraConfig["cameras"][number]>
  ) => {
    const cameras = [...cameraConfig.cameras];
    cameras[index] = { ...cameras[index], ...patch };
    setCameraConfig({ ...cameraConfig, cameras });
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <VideocamIcon color="primary" />
          <Box>
            <Typography variant="h6">Kamera-Einstellungen</Typography>
            <Typography variant="body2" color="text.secondary">
              Stream-Zugang und Kamera-Rollen für Anwesenheits- und Hebebühnenprüfungen.
            </Typography>
          </Box>
        </Stack>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={cameraConfig.enabled}
                  onChange={(event) => setCameraConfig({ ...cameraConfig, enabled: event.target.checked })}
                />
              }
              label="Kamera-Checks aktiv"
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Stream-Basis-URL"
                value={cameraConfig.baseUrl}
                onChange={(event) => setCameraConfig({ ...cameraConfig, baseUrl: event.target.value })}
                placeholder="https://hobby-garage-emin.csnetworkx.de"
                fullWidth
              />
              <TextField
                label="Zugangs-Token (key)"
                type="password"
                value={cameraConfig.key}
                onChange={(event) => setCameraConfig({ ...cameraConfig, key: event.target.value })}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
              <TextField
                label="Client-ID (cid)"
                value={cameraConfig.cid}
                onChange={(event) => setCameraConfig({ ...cameraConfig, cid: event.target.value })}
                sx={{ maxWidth: { sm: 280 } }}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cameraConfig.warm}
                    onChange={(event) => setCameraConfig({ ...cameraConfig, warm: event.target.checked })}
                  />
                }
                label="Streams vor dem Check aufwärmen"
              />
            </Stack>

            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={700}>Kameras</Typography>
              <Button
                size="small"
                onClick={() =>
                  setCameraConfig({
                    ...cameraConfig,
                    cameras: [...cameraConfig.cameras, { id: "", label: "", role: "both" }],
                  })
                }
              >
                + Kamera
              </Button>
            </Stack>

            <Stack spacing={1}>
              {cameraConfig.cameras.map((cam, index) => (
                <Stack key={index} direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }}>
                  <TextField
                    label="Pfad-ID"
                    value={cam.id}
                    onChange={(event) => updateCameraAt(index, { id: event.target.value })}
                    placeholder="cam3"
                    sx={{ maxWidth: { md: 140 } }}
                  />
                  <TextField
                    label="Bezeichnung"
                    value={cam.label}
                    onChange={(event) => updateCameraAt(index, { label: event.target.value })}
                    placeholder="Werkstatt (Tor)"
                    fullWidth
                  />
                  <TextField
                    select
                    label="Auswertung"
                    value={cam.role}
                    onChange={(event) => updateCameraAt(index, { role: event.target.value as PresenceCameraRole })}
                    sx={{ minWidth: { md: 190 } }}
                  >
                    {(Object.keys(CAMERA_ROLE_LABELS) as PresenceCameraRole[]).map((role) => (
                      <MenuItem key={role} value={role}>{CAMERA_ROLE_LABELS[role]}</MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title="Kamera entfernen">
                    <IconButton
                      onClick={() =>
                        setCameraConfig({
                          ...cameraConfig,
                          cameras: cameraConfig.cameras.filter((_, i) => i !== index),
                        })
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
              {cameraConfig.cameras.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Noch keine Kameras konfiguriert.
                </Typography>
              )}
            </Stack>

            <Divider />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={saveCameraConfig}
                disabled={saving}
              >
                {saving ? "Speichert..." : "Speichern"}
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default PresenceCameraSettings;
